import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowLeft, Gift } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, getPublicSettings, googleLogin } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";
import GoogleSignInButton from "../../components/GoogleSignInButton";

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [referralFromLink, setReferralFromLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleError, setGoogleError] = useState("");

  // Admin-controlled from Platform Settings: whether "Continue with Google"
  // shows at all, and which channel the registration OTP below goes out
  // on. These defaults are the safe fallback while the call hasn't
  // resolved yet (or fails) — Google button stays hidden, OTP assumed to
  // go to email like it always has.
  const [settings, setSettings] = useState({
    googleLoginEnabled: false,
    googleClientId: null,
    otpChannel: "EMAIL",
  });

  useEffect(() => {
    getPublicSettings()
      .then((data) => setSettings((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const handleGoogleIdToken = async (idToken) => {
    setGoogleError("");
    try {
      const data = await googleLogin(idToken);
      login(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setGoogleError(err.response?.data?.message || "Google sign-up failed. Please try again.");
    }
  };

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    referralCode: "",
  });

  const [error, setError] = useState("");

  const isValid =
    form.fullName.trim().length > 1 &&
    form.email.length > 5 &&
    form.phone.length > 9 &&
    form.password.length >= 6;

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setForm((prev) => ({ ...prev, referralCode: ref.trim().toUpperCase() }));
      setShowReferral(true);
      setReferralFromLink(true);
    }
  }, [searchParams]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password.length < 6) {
      setError("Your password must contain at least 6 characters.");
      return;
    }

    try {
      setError("");
      setLoading(true);

      const promoCode = form.referralCode.trim().toUpperCase();

      const payload = {
        fullName: form.fullName.trim(),
        email: form.email,
        phone: form.phone,
        password: form.password,
        // NOTE: promo.service's attachSignupCode/applyPromoCode both take a
        // `code` param, so the signup-time promo code is sent as
        // `promoCode` here rather than `referralCode`. Confirm this matches
        // whatever key authService.startRegistration actually reads off
        // req.body — rename this one key if it expects something else.
        ...(promoCode && { promoCode }),
      };

      const response = await apiClient.post("/auth/register", payload);

      console.log("Registration successful:", response.data);

      navigate("/verify", {
        state: {
          email: form.email,
          phone: form.phone,
          // Which channel the OTP that was just sent actually went to —
          // the verify screen needs this to know whether to submit
          // { phone, otp } or { email, otp }. Falling back to the
          // response's own otpChannel (set by whichever channel was
          // active on the server at the moment of registration) rather
          // than the client's local `settings` state, since that's the
          // one guaranteed to match what actually happened.
          otpChannel: response.data?.otpChannel || settings.otpChannel,
          // Carried through so the verify screen can show "promo applied"
          // messaging once OTP verification actually triggers the reward
          // (attachSignupCode + handleTriggerEvent run post-verification,
          // not at registration time — and a bad code fails silently, so
          // we can only say "code submitted", never "code confirmed", here).
          promoCode: promoCode || null,
        },
      });
    } catch (error) {
      console.error("Registration error:", error);

      if (error.response) {
        setError(error.response.data.message || "Registration failed. Please try again.");
      } else if (error.request) {
        setError("No response from server. Check your internet connection.");
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const inputVariants = {
    focus: { scale: 1.02, boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.3)" },
  };

  const errorVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const inputClass = (hasError) =>
    `w-full rounded-full px-5 py-4 bg-white border-2 transition-all duration-200 outline-none text-gray-900 placeholder-gray-400 shadow-sm focus:shadow-md ${
      hasError
        ? "border-red-500 focus:ring-red-200"
        : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
    }`;

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageVariants}
      className="min-h-screen bg-[#F5F6FA] flex flex-col px-5 sm:px-6 pt-6 pb-10"
    >
      <div className="flex-1">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </motion.button>
          <div className="w-6" />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sign up</h1>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5 flex-1">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full name
            </label>
            <motion.input
              type="text"
              placeholder="Enter your full name"
              value={form.fullName}
              onChange={handleChange("fullName")}
              className={inputClass(!!error)}
              whileFocus="focus"
              variants={inputVariants}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email address
            </label>
            <motion.input
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={handleChange("email")}
              className={inputClass(!!error)}
              whileFocus="focus"
              variants={inputVariants}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone number
            </label>
            <motion.input
              type="tel"
              placeholder="Enter phone number"
              value={form.phone}
              onChange={handleChange("phone")}
              className={inputClass(!!error)}
              whileFocus="focus"
              variants={inputVariants}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <motion.div
              className={`flex items-center rounded-full border-2 px-5 py-4 bg-white transition-all duration-200 shadow-sm focus-within:shadow-md ${
                error
                  ? "border-red-500 focus-within:ring-red-200"
                  : "border-gray-300 focus-within:border-blue-500 focus-within:ring-blue-200"
              }`}
              whileFocus="focus"
              variants={inputVariants}
            >
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Set password"
                value={form.password}
                onChange={handleChange("password")}
                className="flex-1 bg-transparent outline-none text-gray-900 placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </motion.div>

            {/* Referral Code */}
            <div className="pt-2">
              {!showReferral ? (
                <motion.button
                  type="button"
                  whileHover={{ x: 5 }}
                  onClick={() => setShowReferral(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  + Have a referral code?
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Referral Code (Optional)
                    </label>
                    {referralFromLink && form.referralCode && (
                      <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                        <Gift size={11} /> Applied from link
                      </span>
                    )}
                  </div>
                  <motion.input
                    type="text"
                    placeholder="e.g. SAVE20"
                    value={form.referralCode}
                    onChange={(e) => {
                      setReferralFromLink(false);
                      setForm((prev) => ({
                        ...prev,
                        referralCode: e.target.value.toUpperCase(),
                      }));
                    }}
                    className="w-full rounded-full px-5 py-4 bg-white border-2 border-gray-300 outline-none text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 transition-all"
                    whileFocus="focus"
                    variants={inputVariants}
                  />
                  <p className="text-xs text-gray-400 px-1">
                    We'll apply any bonus tied to this code once you verify your email.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.p
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={errorVariants}
                  className="text-sm text-red-600 mt-2 font-medium"
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Terms */}
          <p className="text-[10px] text-gray-500 text-center mt-6 leading-relaxed px-4">
            By signing up, you agree to our{" "}
            <span className="underline">Terms of Service</span> and{" "}
            <span className="underline">Privacy Policy</span>
          </p>

          {/* Continue button */}
          <motion.button
            type="submit"
            disabled={!isValid || loading}
            className={`w-full py-4 rounded-full font-semibold text-base shadow-md transition-all duration-300 ${
              isValid
                ? "bg-[#344054] text-white hover:bg-[#2a3745]"
                : "bg-[#D0D5DD] text-gray-500 cursor-not-allowed"
            }`}
          >
            {loading ? "Registering..." : "Continue"}
          </motion.button>

          {/* Google sign-up — only rendered when the admin has turned this
              on (and configured a Client ID) in Platform Settings */}
          {settings.googleLoginEnabled && settings.googleClientId && (
            <div className="pt-1">
              <div className="flex items-center my-6">
                <div className="flex-1 h-[1px] bg-gray-200" />
                <span className="px-3 text-gray-400 text-xs font-medium uppercase tracking-wider">
                  Or
                </span>
                <div className="flex-1 h-[1px] bg-gray-200" />
              </div>
              <GoogleSignInButton
                clientId={settings.googleClientId}
                onIdToken={handleGoogleIdToken}
                onError={(err) => setGoogleError(err.message)}
                text="signup_with"
              />
              {googleError && (
                <p className="text-sm text-red-600 mt-3 font-medium text-center">{googleError}</p>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 h-[1px] bg-gray-200" />
            <span className="px-3 text-gray-400 text-xs font-medium uppercase tracking-wider">
              Have an account?
            </span>
            <div className="flex-1 h-[1px] bg-gray-200" />
          </div>

          {/* Log in button */}
          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/login")}
            className="w-full py-4 rounded-full font-semibold text-base border-2 border-[#344054] text-[#344054] hover:bg-gray-50 transition-all"
          >
            Log in to Account
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
}